import React from 'react';
import PropTypes from 'prop-types';
import { RichUtils } from 'draft-js';
import {
  Select, MenuItem, makeStyles,
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import TooltipIconButton from '../../../widgets/TooltipIconButton';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(3),
  },
  buttonActive: {
    backgroundColor: theme.palette.action.selected,
  },
  tooltipIconButton: {
    display: 'inline-block',
  },
}));

const MyInputHTMLToolbar = (props) => {
  const {
    editorState,
    onChangeHeading,
    inlineStyles,
    onToggleInlineStyle,
    blockStyles,
    onToggleBlockStyle,
    icons,
    onClickUndo,
    onClickRedo,

    onClickAddLink,
    onClickEditLink,
    isCursorOnLink,
    isSelectingText,

    enableImage,
    onClickAddImage,
    onClickEditImage,
    isSelectingImage,

    currentAlignment,
    alignmentStyles,
    onChangeAlignment,

    disabled,
  } = props;
  const classes = useStyles();

  let linkTitle;
  if (isCursorOnLink) {
    linkTitle = 'myInputHTML.toolbar.link.edit';
  } else if (isSelectingText) {
    linkTitle = 'myInputHTML.toolbar.link.add';
  } else {
    linkTitle = 'myInputHTML.toolbar.link.selectText';
  }

  return (
    <div className={classes.container}>
      <Select
        error={false}
        disabled={disabled}
        value={RichUtils.getCurrentBlockType(editorState)}
        onChange={onChangeHeading}
      >
        <MenuItem value="unstyled">Normal</MenuItem>
        <MenuItem value="header-one">H1</MenuItem>
        <MenuItem value="header-two">H2</MenuItem>
        <MenuItem value="header-three">H3</MenuItem>
        <MenuItem value="header-four">H4</MenuItem>
        <MenuItem value="header-five">H5</MenuItem>
        <MenuItem value="header-six">H6</MenuItem>
        <MenuItem value="code-block">Code</MenuItem>
        <MenuItem value="unordered-list-item">Bullets</MenuItem>
        <MenuItem value="ordered-list-item">Numbering</MenuItem>
        <MenuItem value="blockquote">Blockquote</MenuItem>
        <MenuItem value="atomic">Image</MenuItem>
      </Select>
      {
        inlineStyles.map((inline) => (
          <TooltipIconButton
            tabIndex="-1"
            key={`my-input-html-inline-style-button-${inline.name}`}
            icon={inline.icon}
            title={inline.label}

            classes={{
              root: editorState.getCurrentInlineStyle().has(inline.name)
                ? classes.buttonActive : '',
            }}
            color={editorState.getCurrentInlineStyle().has(inline.name) ? 'primary' : 'default'}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault();
              onToggleInlineStyle(inline.name);
            }}
            size="small"

            tooltipProps={{
              className: classes.tooltipIconButton,
            }}
          />
        ))
      }
      {
        blockStyles.map((block) => (
          <TooltipIconButton
            tabIndex="-1"
            key={`my-input-html-block-style-button-${block.name}`}
            icon={block.icon}
            title={block.label}

            classes={{
              root: RichUtils.getCurrentBlockType(editorState) === block.name
                ? classes.active : '',
            }}
            color={
              RichUtils.getCurrentBlockType(editorState) === block.name
                ? 'primary' : 'default'
            }
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault();
              onToggleBlockStyle(block.name);
            }}
            size="small"

            tooltipProps={{
              className: classes.tooltipIconButton,
            }}
          />
        ))
      }
      {/* UNDO */}
      <TooltipIconButton
        tabIndex="-1"
        icon={<icons.Undo />}
        title={<FormattedMessage id="myInputHTML.toolbar.undo" />}

        color="default"
        disabled={editorState.getUndoStack().size <= 0 || disabled}
        onMouseDown={onClickUndo}
        size="small"

        tooltipProps={{
          className: classes.tooltipIconButton,
        }}
      />
      {/* REDO */}
      <TooltipIconButton
        tabIndex="-1"
        icon={<icons.Redo />}
        title={<FormattedMessage id="myInputHTML.toolbar.redo" />}

        color="default"
        disabled={editorState.getRedoStack().size <= 0 || disabled}
        onMouseDown={onClickRedo}
        size="small"

        tooltipProps={{
          className: classes.tooltipIconButton,
        }}
      />
      {/* LINK */}
      <TooltipIconButton
        tabIndex="-1"
        icon={<icons.Link />}
        title={<FormattedMessage id={linkTitle} />}

        classes={{ root: isCursorOnLink ? classes.active : '' }}
        disabled={!isCursorOnLink && !isSelectingText}
        onClick={isCursorOnLink ? onClickEditLink : onClickAddLink}
        size="small"

        tooltipProps={{
          className: classes.tooltipIconButton,
        }}
      />
      {/* IMAGE */}
      {
        !enableImage ? '' : (
          <TooltipIconButton
            tabIndex="-1"
            icon={<icons.Image />}
            title={<FormattedMessage id={isSelectingImage ? 'myInputHTML.toolbar.image.edit' : 'myInputHTML.toolbar.image.add'} />}

            classes={{ root: isSelectingImage ? classes.active : '' }}
            color={isSelectingImage ? 'primary' : 'default'}
            disabled={disabled}
            onClick={isSelectingImage
              ? onClickEditImage : onClickAddImage}
            size="small"

            tooltipProps={{
              className: classes.tooltipIconButton,
            }}
          />
        )
      }
      {/* ALIGNMENTS */}
      {
        alignmentStyles.map((alignment) => (
          <TooltipIconButton
            tabIndex="-1"
            key={`my-input-html-alignment-style-button-${alignment.name}`}
            icon={alignment.icon}
            title={alignment.label}

            classes={{ root: alignment === alignment.name ? classes.active : '' }}
            color={currentAlignment === alignment.name ? 'primary' : 'default'}
            disabled={disabled || isSelectingImage}
            onMouseDown={(e) => {
              e.preventDefault();
              onChangeAlignment(alignment.name);
            }}
            size="small"

            tooltipProps={{
              className: classes.tooltipIconButton,
            }}
          />
        ))
      }
    </div>
  );
};

const myInputHTMLToolbarButtonPropTypes = PropTypes.shape({
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
});

MyInputHTMLToolbar.propTypes = {
  editorState: PropTypes.any.isRequired,
  onChangeHeading: PropTypes.func.isRequired,
  inlineStyles: PropTypes.arrayOf(
    myInputHTMLToolbarButtonPropTypes,
  ),
  onToggleInlineStyle: PropTypes.func.isRequired,
  blockStyles: PropTypes.arrayOf(
    myInputHTMLToolbarButtonPropTypes,
  ),
  onToggleBlockStyle: PropTypes.func.isRequired,
  icons: PropTypes.shape({
    Undo: PropTypes.elementType.isRequired,
    Redo: PropTypes.elementType.isRequired,
    Link: PropTypes.elementType.isRequired,
    Image: PropTypes.elementType.isRequired,
  }).isRequired,
  onClickUndo: PropTypes.func.isRequired,
  onClickRedo: PropTypes.func.isRequired,

  onClickAddLink: PropTypes.func.isRequired,
  onClickEditLink: PropTypes.func.isRequired,
  isCursorOnLink: PropTypes.bool,
  isSelectingText: PropTypes.bool,

  enableImage: PropTypes.bool,
  onClickAddImage: PropTypes.func.isRequired,
  onClickEditImage: PropTypes.func.isRequired,
  isSelectingImage: PropTypes.bool,

  currentAlignment: PropTypes.string.isRequired,
  alignmentStyles: PropTypes.arrayOf(
    myInputHTMLToolbarButtonPropTypes,
  ),
  onChangeAlignment: PropTypes.func.isRequired,

  disabled: PropTypes.bool,
};

MyInputHTMLToolbar.defaultProps = {
  inlineStyles: [],
  blockStyles: [],

  isCursorOnLink: false,
  isSelectingText: false,

  enableImage: true,
  isSelectingImage: false,

  alignmentStyles: [],
  disabled: false,
};

export default MyInputHTMLToolbar;
